import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormText,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import classNames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { ApiError, requestJson } from "../../../helpers/api";
import { obtenerEmpresaActivaId } from "../../../utils/empresa";

type InventarioDetalle = {
  id: number;
  depositoSalida: string;
  codigoItem: string;
  unidad: string;
  cantidad: string;
  descripcion: string;
};

type InventarioResumen = {
  id: string;
  concepto: string;
  sucursal: string;
  fecha: string;
  hora: string;
  estado: "en proceso" | "completado" | "borrador";
  usuarioRegistro: string;
  registradoEn: string;
};

type CatalogoDeposito = {
  id: number;
  fuente: string;
  empresaId: number;
};

type CatalogoArticulo = {
  id: number;
  descripcion: string;
  sku: string | null;
};

const INVENTARIO_TABS = [
  {
    id: "formulario",
    label: "Registrar inventario",
    descripcion: "Cargá un nuevo proceso de inventario.",
    icono: "ri-clipboard-line",
  },
  {
    id: "listado",
    label: "Listado de inventarios",
    descripcion: "Consultá el historial y estados.",
    icono: "ri-file-list-3-line",
  },
];

export type InventarioPageConfig = {
  documentTitle: string;
  breadcrumbTitle: string;
  breadcrumbSection: string;
  conceptoEditable?: boolean;
  conceptoPredeterminado?: string;
  contextLabel: string;
  contextPluralLabel: string;
  headerTitle?: string;
  headerDescription?: string;
  detailTitle?: string;
  detailDescription?: string;
  tabRegisterLabel?: string;
  tabListLabel?: string;
  tabRegisterLabelMobile?: string;
  tabListLabelMobile?: string;
  apiPath: string;
  empresaId?: number;
};

interface InventarioBaseProps {
  config: InventarioPageConfig;
}

const InventarioBase: React.FC<InventarioBaseProps> = ({ config }) => {
  useEffect(() => {
    document.title = config.documentTitle;
  }, [config.documentTitle]);

  const conceptoEditable = config.conceptoEditable ?? true;
  const conceptoPredeterminado = config.conceptoPredeterminado ?? "";
  const contextLabel = config.contextLabel;
  const contextPluralLabel = config.contextPluralLabel;
  const contextLabelLower = contextLabel.toLowerCase();
  const contextPluralLower = contextPluralLabel.toLowerCase();
  const headerTitle = config.headerTitle ?? `Cabecera del ${contextLabelLower}`;
  const headerDescription =
    config.headerDescription ?? "Definí los datos principales antes de cargar los artículos.";
  const detailTitle = config.detailTitle ?? `Detalle del ${contextLabelLower}`;
  const detailDescription =
    config.detailDescription ??
    "Incluí cada artículo contado y el depósito/salida correspondiente.";
  const tabRegisterLabel = config.tabRegisterLabel ?? `Registrar ${contextLabelLower}`;
  const tabListLabel = config.tabListLabel ?? `Listado de ${contextPluralLower}`;
  const tabRegisterLabelMobile = config.tabRegisterLabelMobile ?? "Registrar";
  const tabListLabelMobile = config.tabListLabelMobile ?? "Listado";

  const [tabActiva, setTabActiva] = useState<string>("formulario");
  const [datosModalOpen, setDatosModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [sucursalFiltro, setSucursalFiltro] = useState("");
  const [fechaFiltro, setFechaFiltro] = useState("");
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);

  const inventarios = useMemo<InventarioResumen[]>(
    () => [
      {
        id: "INV-2026-001",
        concepto: "Inventario parcial",
        sucursal: "Casa Central",
        fecha: "2026-01-18",
        hora: "09:45",
        estado: "completado",
        usuarioRegistro: "Mayra Riveros",
        registradoEn: "2026-01-18T15:10:00",
      },
      {
        id: "INV-2026-002",
        concepto: "Control inicial",
        sucursal: "Shopping del Sol",
        fecha: "2026-01-20",
        hora: "13:15",
        estado: "en proceso",
        usuarioRegistro: "Carlos López",
        registradoEn: "2026-01-20T17:45:00",
      },
      {
        id: "INV-2026-003",
        concepto: "Inventario parcial",
        sucursal: "Casa Central",
        fecha: "2026-01-21",
        hora: "08:10",
        estado: "borrador",
        usuarioRegistro: "María Acosta",
        registradoEn: "2026-01-21T08:30:00",
      },
    ],
    [],
  );

  const inventariosFiltrados = useMemo(() => {
    return inventarios.filter((registro) => {
      const coincideBusqueda =
        busqueda.trim().length === 0 ||
        registro.id.toLowerCase().includes(busqueda.toLowerCase()) ||
        registro.concepto.toLowerCase().includes(busqueda.toLowerCase());
      const coincideSucursal = !sucursalFiltro || registro.sucursal === sucursalFiltro;
      const coincideFecha = !fechaFiltro || registro.fecha === fechaFiltro;
      return coincideBusqueda && coincideSucursal && coincideFecha;
    });
  }, [busqueda, fechaFiltro, inventarios, sucursalFiltro]);

  const crearCabeceraInicial = () => ({
    concepto: conceptoEditable ? "" : conceptoPredeterminado,
    fecha: "",
    hora: "",
    sucursal: "",
    numeroInventario: "",
    pedidoRelacionado: "",
    referencia: "",
  });

  const crearDetalleVacio = (): InventarioDetalle => ({
    id: Date.now(),
    depositoSalida: "",
    codigoItem: "",
    unidad: "",
    cantidad: "",
    descripcion: "",
  });

  const [cabecera, setCabecera] = useState(crearCabeceraInicial);

  useEffect(() => {
    if (!conceptoEditable) {
      setCabecera((prev) => ({ ...prev, concepto: conceptoPredeterminado }));
    }
  }, [conceptoEditable, conceptoPredeterminado]);

  const [detalles, setDetalles] = useState<InventarioDetalle[]>([crearDetalleVacio()]);
  const [guardando, setGuardando] = useState(false);
  const [alerta, setAlerta] = useState<{ tipo: "success" | "danger"; mensaje: string } | null>(null);
  const empresaIdActivo = useMemo(
    () => config.empresaId ?? obtenerEmpresaActivaId() ?? 1,
    [config.empresaId],
  );

  const [catalogos, setCatalogos] = useState<{
    depositos: CatalogoDeposito[];
    articulos: CatalogoArticulo[];
  }>({
    depositos: [],
    articulos: [],
  });
  const [catalogosCargando, setCatalogosCargando] = useState(false);
  const [catalogosError, setCatalogosError] = useState<string | null>(null);
  const parseNumero = (valor: string) => {
    if (!valor) return null;
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : null;
  };

  const construirPayload = () => {
    if (!cabecera.fecha) {
      throw new Error("Ingresá la fecha del inventario.");
    }

    const items = detalles
      .map((detalle) => {
        const cantidad = Number(detalle.cantidad);
        const cantidadFisica = Number.isFinite(cantidad) ? cantidad : null;
        return {
          productoId: detalle.codigoItem?.trim() || null,
          unidadId: parseNumero(detalle.unidad),
          cantidadFisica,
          cantidadTeorica: null,
          observacion: detalle.descripcion?.trim() || null,
        };
      })
      .filter(
        (item) =>
          (item.cantidadFisica != null && item.cantidadFisica !== 0) ||
          (item.productoId && item.productoId.length > 0) ||
          (item.observacion && item.observacion.length > 0),
      );

    if (items.length === 0) {
      throw new Error("Agregá al menos un artículo con cantidad.");
    }

    return {
      empresaId: empresaIdActivo,
      depositoId: parseNumero(cabecera.sucursal),
      fecha: cabecera.fecha,
      hora: cabecera.hora || "00:00",
      numeroReferencia: cabecera.numeroInventario || null,
      pedidoReferencia: cabecera.pedidoRelacionado || null,
      referencia: cabecera.referencia || null,
      items,
    };
  };

  const resetFormulario = () => {
    setCabecera(crearCabeceraInicial());
    setDetalles([crearDetalleVacio()]);
  };

  useEffect(() => {
    let activo = true;
    const cargarCatalogos = async () => {
      setCatalogosCargando(true);
      setCatalogosError(null);
      try {
        const baseUrl = `/api/stock/catalogos`;
        const qs = `empresaId=${empresaIdActivo}`;
        const [depositosData, articulosData] = await Promise.all([
          requestJson<CatalogoDeposito[]>(`${baseUrl}/depositos?${qs}`),
          requestJson<CatalogoArticulo[]>(`${baseUrl}/articulos?${qs}`),
        ]);
        if (!activo) return;
        setCatalogos({
          depositos: depositosData,
          articulos: articulosData,
        });
      } catch (error) {
        if (!activo) return;
        const mensaje =
          error instanceof ApiError
            ? error.message
            : "No se pudieron cargar los catálogos.";
        setCatalogosError(mensaje);
      } finally {
        if (activo) {
          setCatalogosCargando(false);
        }
      }
    };
    cargarCatalogos();
    return () => {
      activo = false;
    };
  }, [empresaIdActivo]);

  const handleCabeceraChange = (campo: keyof typeof cabecera, valor: string) => {
    if (!conceptoEditable && campo === "concepto") {
      return;
    }
    setCabecera((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleDetalleChange = (id: number, campo: keyof InventarioDetalle, valor: string) => {
    setDetalles((prev) =>
      prev.map((detalle) => (detalle.id === id ? { ...detalle, [campo]: valor } : detalle)),
    );
  };

  const agregarLineaDetalle = () => {
    setDetalles((prev) => [...prev, crearDetalleVacio()]);
  };

  const eliminarLineaDetalle = (id: number) => {
    setDetalles((prev) => (prev.length === 1 ? prev : prev.filter((detalle) => detalle.id !== id)));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setAlerta(null);

    let payload;
    try {
      payload = construirPayload();
    } catch (error) {
      setAlerta({
        tipo: "danger",
        mensaje: error instanceof Error ? error.message : "Completá los datos obligatorios.",
      });
      return;
    }

    setGuardando(true);
    try {
      await requestJson(config.apiPath, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setAlerta({
        tipo: "success",
        mensaje: `${contextLabel} registrado correctamente.`,
      });
      resetFormulario();
      setTabActiva("listado");
    } catch (error) {
      const mensaje =
        error instanceof ApiError
          ? error.message
          : "No se pudo registrar el inventario. Intentá nuevamente.";
      setAlerta({ tipo: "danger", mensaje });
    } finally {
      setGuardando(false);
    }
  };

  const renderListado = () => (
    <Card>
      <CardHeader className="border-0">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="flex-grow-1">
              <h5 className="card-title mb-0">Listado de {contextPluralLower} recientes</h5>
            <p className="text-muted mb-0">
              Consultá todos los {contextPluralLower} registrados en cada depósito.
            </p>
            </div>
            <Button color="soft-secondary" onClick={() => setFiltrosVisibles(true)}>
              <i className="ri-equalizer-line align-middle me-1" />
              Filtros de {contextPluralLower}
            </Button>
          </div>
          </CardHeader>
          <CardBody>
        <div className="text-muted mb-3">
          Mostrando <strong>{inventariosFiltrados.length}</strong> de <strong>{inventarios.length}</strong>{" "}
          {contextPluralLower}.
        </div>
        <Offcanvas direction="end" isOpen={filtrosVisibles} toggle={() => setFiltrosVisibles(false)} scrollable>
          <OffcanvasHeader toggle={() => setFiltrosVisibles(false)}>
            Filtros de {contextPluralLower}
          </OffcanvasHeader>
          <OffcanvasBody>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                setFiltrosVisibles(false);
              }}
            >
              <div className="mb-3">
                <Label htmlFor="inventario-busqueda" className="form-label">
                  Buscar por código o concepto
                </Label>
                <Input
                  id="inventario-busqueda"
                  value={busqueda}
                  placeholder="Ej: INV-2026..."
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <Label htmlFor="inventario-sucursal" className="form-label">
                  Depósito
                </Label>
                <Input
                  id="inventario-sucursal"
                  type="select"
                  value={sucursalFiltro}
                  onChange={(e) => setSucursalFiltro(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="Casa Central">Casa Central</option>
                  <option value="Shopping del Sol">Shopping del Sol</option>
                </Input>
              </div>
              <div className="mb-4">
                <Label htmlFor="inventario-fecha" className="form-label">
                  Fecha exacta
                </Label>
                <Input
                  id="inventario-fecha"
                  type="date"
                  value={fechaFiltro}
                  onChange={(e) => setFechaFiltro(e.target.value)}
                />
              </div>
              <div className="d-flex justify-content-between gap-2 flex-wrap">
                <Button
                  type="button"
                  color="link"
                  className="text-decoration-none text-reset"
                  onClick={() => {
                    setBusqueda("");
                    setSucursalFiltro("");
                    setFechaFiltro("");
                  }}
                >
                  Limpiar filtros
                </Button>
                <Button type="submit" color="primary">
                  Aplicar filtros
                </Button>
             </div>
            </Form>
          </OffcanvasBody>
        </Offcanvas>

        <div className="d-flex flex-column flex-lg-row align-items-stretch gap-3 mb-4">
          <Input
            type="search"
            className="flex-grow-1"
            placeholder={`Buscar ${contextPluralLower}`}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <Button color="success" className="flex-grow-0 text-white" style={{ minWidth: 150 }}>
            <i className="ri-download-2-line align-middle me-1" />
            Exportar Excel
          </Button>
        </div>

        <div className="table-responsive table-card pt-3">
          <Table hover className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Código</th>
                <th>Concepto</th>
                <th>Depósito</th>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Estado</th>
                <th>Registrado por</th>
                <th>Registrado el</th>
                <th style={{ width: "110px" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {inventariosFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No encontramos inventarios con los filtros seleccionados.
                  </td>
                </tr>
              )}
              {inventariosFiltrados.map((inventario) => (
                <tr key={inventario.id}>
                  <td className="fw-semibold">{inventario.id}</td>
                  <td>{inventario.concepto}</td>
                  <td>{inventario.sucursal}</td>
                  <td>{inventario.fecha}</td>
                  <td>{inventario.hora}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className={classNames("avatar-xxs rounded-circle d-inline-flex align-items-center justify-content-center", {
                          "bg-success-subtle text-success": inventario.estado === "completado",
                          "bg-warning-subtle text-warning": inventario.estado === "en proceso",
                          "bg-secondary-subtle text-secondary": inventario.estado === "borrador",
                        })}
                      >
                        <i
                          className={classNames("ri-check-line", {
                            "ri-check-line": inventario.estado === "completado",
                            "ri-time-line": inventario.estado === "en proceso",
                            "ri-draft-line": inventario.estado === "borrador",
                          })}
                        />
                      </span>
                      <span className="text-capitalize">{inventario.estado}</span>
                    </div>
                  </td>
                  <td>
                    <div className="fw-semibold">{inventario.usuarioRegistro}</div>
                    <div className="text-muted fs-12">Usuario</div>
                  </td>
                  <td>
                    <div className="fw-semibold">
                      {new Date(inventario.registradoEn).toLocaleDateString("es-PY", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </div>
                    <div className="text-muted fs-12">
                      {new Date(inventario.registradoEn).toLocaleTimeString("es-PY", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button size="sm" color="soft-primary">
                        Ver
                      </Button>
                      <Button size="sm" color="soft-secondary">
                        Copiar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
       </div>
      </CardBody>
    </Card>
  );

  const renderDatosPrincipales = () => (
    <Row className="g-4 align-items-start">
      <Col xxl={2} lg={4} md={6}>
        <Label htmlFor="concepto" className="form-label fw-semibold">
          Concepto <span className="text-danger">*</span>
        </Label>
        <Input
          id="concepto"
          type="text"
          value={cabecera.concepto}
          placeholder="Ej: Inventario parcial"
          onChange={(e) => handleCabeceraChange("concepto", e.target.value)}
          disabled={!conceptoEditable}
        />
        <FormText>
          {conceptoEditable
            ? "Elegí el alcance del conteo para agrupar los reportes."
            : `Siempre se registra como "${conceptoPredeterminado}".`}
        </FormText>
      </Col>
      <Col xxl={2} lg={4} md={6}>
        <Label htmlFor="sucursal" className="form-label fw-semibold">
          Depósito <span className="text-danger">*</span>
        </Label>
        <Input
          id="sucursal"
          type="select"
          value={cabecera.sucursal}
          onChange={(e) => handleCabeceraChange("sucursal", e.target.value)}
        >
          <option value="">Seleccioná...</option>
          {catalogos.depositos.map((deposito) => (
            <option key={deposito.id} value={String(deposito.id)}>
              {deposito.fuente}
            </option>
          ))}
        </Input>
        <FormText>
          {catalogosCargando ? "Cargando depósitos..." : "Esta selección define el depósito a inventariar."}
        </FormText>
        {catalogosError && <FormText color="danger">{catalogosError}</FormText>}
      </Col>
      <Col xxl={2} lg={4} md={6}>
        <Label htmlFor="fecha" className="form-label fw-semibold">
          Fecha <span className="text-danger">*</span>
        </Label>
        <InputGroup>
          <InputGroupText>
            <i className="ri-calendar-line" />
          </InputGroupText>
          <Input
            id="fecha"
            type="date"
            value={cabecera.fecha}
            onChange={(e) => handleCabeceraChange("fecha", e.target.value)}
          />
        </InputGroup>
        <FormText>Corresponde al día en que se ejecutó el conteo.</FormText>
      </Col>
      <Col xxl={2} lg={4} md={6}>
        <Label htmlFor="hora" className="form-label fw-semibold">
          Hora <span className="text-danger">*</span>
        </Label>
        <InputGroup>
          <InputGroupText>
            <i className="ri-time-line" />
          </InputGroupText>
          <Input
            id="hora"
            type="time"
            value={cabecera.hora}
            onChange={(e) => handleCabeceraChange("hora", e.target.value)}
          />
        </InputGroup>
        <FormText>Registrá la hora de cierre para rastrear el turno responsable.</FormText>
      </Col>
      <Col xxl={2} lg={4} md={6}>
        <Label htmlFor="numero-inventario" className="form-label fw-semibold">
          Nro. de inventario
        </Label>
        <InputGroup>
          <InputGroupText>#</InputGroupText>
          <Input
            id="numero-inventario"
            value={cabecera.numeroInventario}
            placeholder="Ej: INV-2026-001"
            onChange={(e) => handleCabeceraChange("numeroInventario", e.target.value)}
          />
        </InputGroup>
        <FormText>Si lo dejás vacío, el sistema podrá autogenerarlo al guardar.</FormText>
      </Col>
      <Col xxl={2} lg={4} md={6}>
        <Label htmlFor="pedido-relacionado" className="form-label fw-semibold">
          Pedido relacionado
        </Label>
        <InputGroup>
          <InputGroupText>
            <i className="ri-external-link-line" />
          </InputGroupText>
          <Input
            id="pedido-relacionado"
            value={cabecera.pedidoRelacionado}
            placeholder="Código de pedido o transferencia asociada"
            onChange={(e) => handleCabeceraChange("pedidoRelacionado", e.target.value)}
          />
        </InputGroup>
        <FormText>Útil para vincular inventarios con órdenes o transferencias existentes.</FormText>
      </Col>
      <Col xs={12}>
        <Label htmlFor="referencia" className="form-label fw-semibold">
          Referencia / Comentarios
        </Label>
        <Input
          id="referencia"
          type="textarea"
          rows={2}
          placeholder="Notas internas, instrucciones o aclaraciones adicionales."
          value={cabecera.referencia}
          onChange={(e) => handleCabeceraChange("referencia", e.target.value)}
        />
        <FormText>
          Compartí aquí cualquier detalle que ayude a justificar diferencias o decisiones tomadas.
        </FormText>
      </Col>
    </Row>
  );

  const renderFormulario = () => (
    <Form onSubmit={handleSubmit}>
      <Card className="mb-3">
        <CardHeader className="border-0">
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-2 align-items-start align-items-lg-center">
            <div className="w-100 w-lg-auto">
              <h5 className="card-title mb-0">{headerTitle}</h5>
              <p className="text-muted mb-0">{headerDescription}</p>
            </div>
            <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 w-100 ms-lg-auto">
              <span className="text-muted small me-md-2 d-none d-lg-inline">
                Los campos marcados con <span className="text-danger">*</span> son obligatorios.
              </span>
              <div className="d-flex flex-nowrap align-items-stretch gap-2 w-100">
                <Button
                  color="light"
                  className="border text-nowrap flex-fill d-md-none"
                  type="button"
                  onClick={() => setDatosModalOpen(true)}
                >
                  Datos
                </Button>
                <Button
                  color="soft-info"
                  type="button"
                  disabled={guardando}
                  className="text-nowrap flex-fill flex-md-grow-0 d-flex justify-content-center align-items-center"
                  title="Usar plantilla"
                >
                  <i className="ri-file-copy-line align-middle me-md-1" />
                  <span className="d-none d-md-inline">Usar plantilla</span>
                </Button>
                <Button
                  color="secondary"
                  type="button"
                  disabled={guardando}
                  className="text-nowrap flex-fill flex-md-grow-0 d-flex justify-content-center align-items-center"
                  title="Cancelar"
                  onClick={() => {
                    resetFormulario();
                    setTabActiva("listado");
                  }}
                >
                  <i className="ri-close-line align-middle me-md-1" />
                  <span className="d-none d-md-inline">Cancelar</span>
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={guardando}
                  className="text-nowrap flex-fill flex-md-grow-0 d-flex justify-content-center align-items-center"
                  title="Guardar"
                >
                  {guardando ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <>
                      <i className="ri-save-line align-middle me-md-1" />
                      <span className="d-none d-md-inline">{`Guardar ${contextLabelLower}`}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
      <CardBody>
        {alerta && (
          <Alert color={alerta.tipo} toggle={() => setAlerta(null)}>
            {alerta.mensaje}
          </Alert>
        )}
        <div className="d-none d-md-block">{renderDatosPrincipales()}</div>
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader className="border-0">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <div>
              <h5 className="card-title mb-0">{detailTitle}</h5>
              <p className="text-muted mb-0">{detailDescription}</p>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="table-responsive">
            <Table className="mb-0 align-middle table-nowrap">
              <thead className="table-light">
                <tr className="d-none d-md-table-row">
                  <th style={{ width: "170px" }}>Salida / Depósito</th>
                  <th style={{ width: "150px" }}>Código</th>
                  <th style={{ width: "140px" }}>Unidad</th>
                  <th style={{ width: "120px" }}>Cantidad</th>
                  <th>Descripción del artículo</th>
                  <th style={{ width: "60px" }}></th>
                </tr>
                <tr className="d-table-row d-md-none">
                  <th>Detalle del artículo</th>
                </tr>
              </thead>
              <tbody>
                {detalles.map((detalle) => (
                  <React.Fragment key={detalle.id}>
                    <tr className="d-none d-md-table-row">
                      <td>
                        <Input
                          type="select"
                          value={detalle.depositoSalida}
                          onChange={(e) => handleDetalleChange(detalle.id, "depositoSalida", e.target.value)}
                        >
                          <option value="">Seleccioná...</option>
                          {catalogos.depositos.map((deposito) => (
                            <option key={deposito.id} value={String(deposito.id)}>
                              {deposito.fuente}
                            </option>
                          ))}
                        </Input>
                      </td>
                      <td>
                        <Input
                          value={detalle.codigoItem}
                          list="inventario-articulos-options"
                          placeholder="Código"
                          onChange={(e) => handleDetalleChange(detalle.id, "codigoItem", e.target.value)}
                        />
                      </td>
                      <td>
                        <Input
                          value={detalle.unidad}
                          placeholder="Unidad"
                          onChange={(e) => handleDetalleChange(detalle.id, "unidad", e.target.value)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={detalle.cantidad}
                          placeholder="0"
                          onChange={(e) => handleDetalleChange(detalle.id, "cantidad", e.target.value)}
                        />
                      </td>
                      <td>
                        <Input
                          value={detalle.descripcion}
                          placeholder="Descripción o notas del artículo"
                          onChange={(e) => handleDetalleChange(detalle.id, "descripcion", e.target.value)}
                        />
                      </td>
                      <td>
                        <Button
                          size="sm"
                          color="soft-danger"
                          disabled={detalles.length === 1}
                          onClick={() => eliminarLineaDetalle(detalle.id)}
                        >
                          <i className="ri-delete-bin-5-line" />
                        </Button>
                      </td>
                    </tr>

                    <tr className="d-table-row d-md-none">
                      <td>
                        <div className="mb-2">
                          <Label className="form-label fw-semibold small mb-1">Descripción</Label>
                          <Input
                            value={detalle.descripcion}
                            placeholder="Descripción del artículo"
                            onChange={(e) => handleDetalleChange(detalle.id, "descripcion", e.target.value)}
                          />
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-2">
                          <div className="flex-grow-1">
                            <Label className="form-label fw-semibold small mb-1">Cantidad</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={detalle.cantidad}
                              placeholder="0"
                              onChange={(e) => handleDetalleChange(detalle.id, "cantidad", e.target.value)}
                            />
                          </div>
                          <Button
                            size="sm"
                            color="soft-danger"
                            disabled={detalles.length === 1}
                            onClick={() => eliminarLineaDetalle(detalle.id)}
                            className="mb-1"
                          >
                            <i className="ri-delete-bin-5-line" />
                          </Button>
                          <div className="flex-grow-1">
                            <Label className="form-label fw-semibold small mb-1">Diferencia</Label>
                            <Input value="" placeholder="" disabled readOnly />
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="p-3 border-top d-flex justify-content-end">
            <Button color="soft-primary" onClick={agregarLineaDetalle}>
              <i className="ri-add-line align-bottom me-1" />
              Agregar línea
            </Button>
          </div>
        </CardBody>
      </Card>
      <Modal isOpen={datosModalOpen} toggle={() => setDatosModalOpen(false)} className="d-md-none">
        <ModalHeader toggle={() => setDatosModalOpen(false)}>Datos del inventario</ModalHeader>
        <ModalBody>{renderDatosPrincipales()}</ModalBody>
      </Modal>
    </Form>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={config.breadcrumbTitle} pageTitle={config.breadcrumbSection} />
        <Row className="g-3 mb-3">
          <Col xs={12}>
            <Nav tabs className="nav nav-tabs nav-border-top nav-border-top-primary">
              {[
                {
                  id: "formulario",
                  icono: "ri-clipboard-line",
                  label: tabRegisterLabel,
                  mobileLabel: tabRegisterLabelMobile,
                },
                {
                  id: "listado",
                  icono: "ri-file-list-3-line",
                  label: tabListLabel,
                  mobileLabel: tabListLabelMobile,
                },
              ].map((tab) => (
                <NavItem key={tab.id}>
                  <NavLink
                    href="#"
                    className={classNames("px-3 py-2", { active: tabActiva === tab.id })}
                    onClick={(e) => {
                      e.preventDefault();
                      setTabActiva(tab.id);
                    }}
                    >
                      <i className={`${tab.icono} align-middle me-2`} />
                      <span className="d-none d-md-inline">{tab.label}</span>
                      <span className="d-inline d-md-none">{tab.mobileLabel}</span>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </Col>
        </Row>
        <Card className="mb-3">
          <CardBody className="pb-0">
            <TabContent activeTab={tabActiva} className="text-muted">
              <TabPane tabId="formulario">{renderFormulario()}</TabPane>
              <TabPane tabId="listado">{renderListado()}</TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default InventarioBase;
